"use client"

import type React from "react"

import { useEffect, useState } from "react"
import { motion } from "framer-motion"
import {
  Heart,
  Mail,
  Phone,
  Calendar,
  Sparkles,
  ArrowRight,
  Gift,
  CheckCircle,
  XCircle,
  AlertCircle,
} from "lucide-react"
import axios from "axios"
import ImagesDev from "../api/images"

interface PartnerFormData {
  full_name: string
  email: string
  phone: string
  partnership_frequency: string
  currency: string
  sponsorship_amount: string
  custom_amount: string
  isEmailAvailable: boolean | null
  isPhoneAvailable: boolean | null
}

const currencies = [
  { code: "USD", symbol: "$", name: "US Dollar" },
  { code: "EUR", symbol: "€", name: "Euro" },
  { code: "GBP", symbol: "£", name: "British Pound" },
  { code: "NGN", symbol: "₦", name: "Nigerian Naira" },
  { code: "KES", symbol: "KSh", name: "Kenyan Shilling" },
  { code: "ZAR", symbol: "R", name: "South African Rand" },
]

const getConvertedAmount = (usdAmount: number, currency: string): string => {
  const rates: Record<string, number> = {
    USD: 1,
    EUR: 0.92,
    GBP: 0.79,
    NGN: 1550,
    KES: 130,
    ZAR: 18,
  }

  const rate = rates[currency] || 1
  const converted = usdAmount * rate

  if (converted >= 1000) {
    return Math.round(converted / 1000) * 1000 + ""
  } else if (converted >= 100) {
    return Math.round(converted / 100) * 100 + ""
  } else {
    return Math.round(converted / 10) * 10 + ""
  }
}

async function checkEmailAvailability(email: string): Promise<boolean | null> {
  try {
    const res = await fetch("https://tsoministries.com/wp-json/custom/v1/get-partners/")
    if (!res.ok) throw new Error("Request failed")

    const data = await res.json()
    if (!Array.isArray(data)) return null

    const exists = data.some(
      (item) => item.email?.toLowerCase() === email.toLowerCase()
    )

    return !exists
  } catch (err) {
    console.error("Email check failed:", err)
    return null
  }
}

async function checkPhoneAvailability(phone: string): Promise<boolean | null> {
  try {
    const res = await fetch("https://tsoministries.com/wp-json/custom/v1/get-partners/")
    if (!res.ok) throw new Error("Request failed")

    const data = await res.json()
    if (!Array.isArray(data)) return null

    const exists = data.some(
      (item) => item.phone === phone
    )

    return !exists
  } catch (err) {
    console.error("Phone check failed:", err)
    return null
  }
}

const PartnerEmailInput = ({ value, onChange, setFormData }: any) => {
  const [email, setEmail] = useState(value || "")
  const [isValid, setIsValid] = useState(true)
  const [isAvailable, setIsAvailable] = useState<boolean | null>(null)
  const [showEmailWarning, setShowEmailWarning] = useState(false)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (!email) {
      setIsValid(true)
      setIsAvailable(null)
      return
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    const isEmailValid = emailRegex.test(email)
    setIsValid(isEmailValid)
    setShowEmailWarning(true)

    if (!isEmailValid) {
      setIsAvailable(null)
      return
    }

    const delay = setTimeout(async () => {
      setLoading(true)
      try {
        const available = await checkEmailAvailability(email)
        setIsAvailable(available)
      } catch (error) {
        console.error("Error checking email availability:", error)
        setIsAvailable(null)
      } finally {
        setLoading(false)
      }
    }, 500)

    return () => clearTimeout(delay)
  }, [email])

  useEffect(() => {
    if (!email) {
      setFormData((prev: any) => ({ ...prev, isEmailAvailable: null }))
      return
    }

    if (isValid) {
      setFormData((prev: any) => ({ ...prev, isEmailAvailable: isAvailable }))
    } else {
      setFormData((prev: any) => ({ ...prev, isEmailAvailable: false }))
    }
  }, [email, isValid, isAvailable, setFormData])

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value)
    onChange(e)
  }

  return (
    <div className="space-y-2">
      <label className="block text-sm font-semibold text-gray-700">
        Email Address <span className="text-red-500">*</span>
      </label>
      <div className="relative">
        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
        <input
          type="email"
          name="email"
          value={email}
          onChange={handleChange}
          className={`w-full pl-11 pr-10 py-3 rounded-xl border-2 ${!isValid && email.length > 0 ? "border-red-400" : isAvailable === false ? "border-red-400" : "border-gray-200"} focus:border-rose-500 focus:ring-4 focus:ring-rose-100 outline-none transition-all`}
          placeholder="you@example.com"
        />
        {loading ? (
          <div className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-xs">Checking...</div>
        ) : (
          showEmailWarning &&
          isValid && (
            <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
              {isAvailable === true ? (
                <CheckCircle className="text-green-500 w-5 h-5" />
              ) : isAvailable === false ? (
                <XCircle className="text-red-500 w-5 h-5" />
              ) : (
                email.length >= 1 && <AlertCircle className="text-yellow-500 w-5 h-5" />
              )}
            </div>
          )
        )}
      </div>

      {showEmailWarning && !isValid && email.length > 0 && (
        <p className="text-red-500 text-sm mt-1">Invalid email format.</p>
      )}

      {showEmailWarning && isValid && isAvailable === false && (
        <p className="text-red-500 text-sm mt-1">This email is already registered. You are already a partner.</p>
      )}

      {email.length >= 1 && showEmailWarning && isValid && isAvailable === null && !loading && (
        <p className="text-yellow-500 text-sm mt-1">Unable to verify email at the moment. Please try again shortly.</p>
      )}
    </div>
  )
}

const PartnerPhoneInput = ({ value, onChange, setFormData }: any) => {
  const [phone, setPhone] = useState(value || "")
  const [isAvailable, setIsAvailable] = useState<boolean | null>(null)
  const [showPhoneWarning, setShowPhoneWarning] = useState(false)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (!phone || phone.length < 5) {
      setIsAvailable(null)
      return
    }

    setShowPhoneWarning(true)

    const delay = setTimeout(async () => {
      setLoading(true)
      try {
        const available = await checkPhoneAvailability(phone)
        setIsAvailable(available)
      } catch (error) {
        console.error("Error checking phone availability:", error)
        setIsAvailable(null)
      } finally {
        setLoading(false)
      }
    }, 500)

    return () => clearTimeout(delay)
  }, [phone])

  useEffect(() => {
    if (!phone) {
      setFormData((prev: any) => ({ ...prev, isPhoneAvailable: null }))
      return
    }

    setFormData((prev: any) => ({ ...prev, isPhoneAvailable: isAvailable }))
  }, [phone, isAvailable, setFormData])

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPhone(e.target.value)
    onChange(e)
  }

  return (
    <div className="space-y-2">
      <label className="block text-sm font-semibold text-gray-700">
        Phone Number (WhatsApp) <span className="text-red-500">*</span>
      </label>
      <div className="relative">
        <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
        <input
          type="tel"
          name="phone"
          value={phone}
          onChange={handleChange}
          className={`w-full pl-11 pr-10 py-3 rounded-xl border-2 ${isAvailable === false ? "border-red-400" : "border-gray-200"} focus:border-rose-500 focus:ring-4 focus:ring-rose-100 outline-none transition-all`}
          placeholder="+1234567890"
        />
        {loading ? (
          <div className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-xs">Checking...</div>
        ) : (
          showPhoneWarning &&
          phone.length >= 5 && (
            <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
              {isAvailable === true ? (
                <CheckCircle className="text-green-500 w-5 h-5" />
              ) : isAvailable === false ? (
                <XCircle className="text-red-500 w-5 h-5" />
              ) : (
                <AlertCircle className="text-yellow-500 w-5 h-5" />
              )}
            </div>
          )
        )}
      </div>

      {showPhoneWarning && isAvailable === false && (
        <p className="text-red-500 text-sm mt-1">This phone number is already registered. You are already a partner.</p>
      )}

      {phone.length >= 5 && showPhoneWarning && isAvailable === null && !loading && (
        <p className="text-yellow-500 text-sm mt-1">Unable to verify phone at the moment. Please try again shortly.</p>
      )}
    </div>
  )
}

export default function PartnerForm() {
  const [formData, setFormData] = useState<PartnerFormData>({
    full_name: "",
    email: "",
    phone: "",
    partnership_frequency: "",
    currency: "USD",
    sponsorship_amount: "",
    custom_amount: "",
    isEmailAvailable: null,
    isPhoneAvailable: null,
  })

  const [errors, setErrors] = useState<Record<string, string>>({})
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitSuccess, setSubmitSuccess] = useState(false)
  const [bgImage, setBgImage] = useState("")

  useEffect(() => {
    setBgImage(getRandomHeroImage())
  }, [])

  const getRandomHeroImage = () => {
    const images = window.innerWidth > 768 ? ImagesDev.heroImagesDesk : ImagesDev.heroImages
    return images[Math.floor(Math.random() * images.length)]
  }

  const handleInputChange = (field: keyof PartnerFormData, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }))

    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: "" }))
    }
  }

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {}

    if (!formData.full_name.trim()) newErrors.full_name = "Full name is required"
    if (!formData.email.trim()) {
      newErrors.email = "Email is required"
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Invalid email format"
    } else if (formData.isEmailAvailable === false) {
      newErrors.email = "This email is already registered"
    }
    if (!formData.phone.trim()) {
      newErrors.phone = "Phone number is required"
    } else if (formData.isPhoneAvailable === false) {
      newErrors.phone = "This phone number is already registered"
    }
    if (!formData.partnership_frequency) newErrors.partnership_frequency = "Please select a frequency"
    if (!formData.currency) newErrors.currency = "Please select a currency"
    if (!formData.sponsorship_amount) newErrors.sponsorship_amount = "Please select an amount"
    if (formData.sponsorship_amount === "custom" && !formData.custom_amount.trim()) {
      newErrors.custom_amount = "Please specify an amount"
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!validateForm()) {
      const firstError = document.querySelector('[data-error="true"]')
      firstError?.scrollIntoView({ behavior: "smooth", block: "center" })
      return
    }

    setIsSubmitting(true)

    try {
      const res = await axios.post("https://tsoministries.com/wp-json/custom/v1/submit-partner/", formData, {
        headers: { "Content-Type": "application/json" },
      })

      if (res.status === 200) {
        setSubmitSuccess(true)
        window.scrollTo({ top: 0, behavior: "smooth" })
      }
    } catch (err: any) {
      console.error(err)
      alert(err.response?.data?.error || "An error occurred. Please try again.")
    } finally {
      setIsSubmitting(false)
    }
  }

  const selectedCurrency = currencies.find((c) => c.code === formData.currency)
  const amountOptions = [20, 50, 100, 200, 500, 1000]

  if (submitSuccess) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-rose-50 via-pink-50 to-purple-100 flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="max-w-2xl w-full bg-white rounded-3xl shadow-2xl p-8 md:p-12 text-center"
        >
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
            className="w-24 h-24 bg-gradient-to-br from-rose-400 to-pink-500 rounded-full mx-auto mb-6 flex items-center justify-center"
          >
            <Heart className="w-14 h-14 text-white fill-white" />
          </motion.div>

          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="text-4xl font-bold text-gray-800 mb-4"
          >
            Thank You, Partner! 💝
          </motion.h2>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="text-gray-600 mb-8 text-lg"
          >
            Your commitment to supporting TSO Ministries means the world to us. Together, we're making an eternal
            impact!
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="bg-gradient-to-r from-rose-50 to-pink-50 rounded-2xl p-6"
          >
            <h3 className="text-2xl font-bold text-gray-800 mb-4 flex items-center justify-center gap-2">
              <Gift className="w-6 h-6 text-rose-600" />
              What Happens Next?
            </h3>
            <div className="space-y-3 text-left max-w-md mx-auto">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-rose-500 text-white rounded-full flex items-center justify-center font-bold flex-shrink-0">
                  1
                </div>
                <p className="text-gray-700 pt-1">Our team will contact you within 24 hours via email or WhatsApp</p>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-rose-500 text-white rounded-full flex items-center justify-center font-bold flex-shrink-0">
                  2
                </div>
                <p className="text-gray-700 pt-1">We'll provide you with payment details and options</p>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-rose-500 text-white rounded-full flex items-center justify-center font-bold flex-shrink-0">
                  3
                </div>
                <p className="text-gray-700 pt-1">
                  You'll receive regular updates on how your partnership is making a difference
                </p>
              </div>
            </div>
          </motion.div>

          <motion.button
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
            onClick={() => (window.location.href = "/")}
            className="mt-8 inline-flex items-center gap-2 bg-gradient-to-r from-rose-500 to-pink-600 text-white px-8 py-4 rounded-full font-semibold hover:shadow-lg hover:scale-105 transition-all duration-300"
          >
            Return to Home
            <ArrowRight className="w-5 h-5" />
          </motion.button>
        </motion.div>
      </div>
    )
  }

  return (
    <div className="relative min-h-screen bg-gradient-to-br from-amber-800 via-amber-800 to-purple-800 py-12 px-4 sm:px-6 lg:px-8">
      <motion.img
        initial={{ opacity: 0 }}
        animate={{ opacity: 0.4 }}
        transition={{ duration: 2 }}
        className="absolute top-0 left-0 bottom-0 h-[100%] w-full"
        src={bgImage}
        style={{ objectFit: "cover" }}
      />
      <div className="relative max-w-3xl mx-auto">
        <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} className="text-center mb-12">
          <div className="opacity-0 inline-flex items-center gap-3 bg-white rounded-full px-6 py-3 shadow-lg mb-6">
            <Heart className="w-6 h-6 text-rose-600 fill-rose-600" />
            <span className="text-rose-600 font-semibold">Partnership Program</span>
          </div>
          <h1 className="text-5xl font-bold text-gray-100 mb-4">
            Become a{" "}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-rose-300 to-pink-300">
              Ministry Partner
            </span>
          </h1>
          <p className="text-gray-200 text-lg max-w-2xl mx-auto">
            Your financial support empowers us to reach more lives and spread the gospel. Join us in making a lasting
            impact for the Kingdom!
          </p>
        </motion.div>

        <motion.form
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          onSubmit={handleSubmit}
          className="bg-white rounded-3xl shadow-2xl p-8 md:p-12 space-y-8"
        >
          {/* Personal Information */}
          <div className="space-y-6">
            <h3 className="text-2xl font-bold text-gray-800 flex items-center gap-2 pb-4 border-b-2 border-rose-200">
              <Gift className="w-6 h-6 text-rose-600" />
              Your Information
            </h3>

            <div className="space-y-2" data-error={!!errors.full_name}>
              <label className="block text-sm font-semibold text-gray-700">
                Full Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.full_name}
                onChange={(e) => handleInputChange("full_name", e.target.value)}
                className={`w-full px-4 py-3 rounded-xl border-2 ${errors.full_name ? "border-red-400" : "border-gray-200"} focus:border-rose-500 focus:ring-4 focus:ring-rose-100 outline-none transition-all`}
                placeholder="Enter your full name"
              />
              {errors.full_name && <p className="text-red-500 text-sm">{errors.full_name}</p>}
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div data-error={!!errors.email}>
                <PartnerEmailInput
                  value={formData.email}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleInputChange("email", e.target.value)}
                  setFormData={setFormData}
                />
                {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email}</p>}
              </div>

              <div data-error={!!errors.phone}>
                <PartnerPhoneInput
                  value={formData.phone}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleInputChange("phone", e.target.value)}
                  setFormData={setFormData}
                />
                {errors.phone && <p className="text-red-500 text-sm mt-1">{errors.phone}</p>}
              </div>
            </div>
          </div>

          {/* Partnership Details */}
          <div className="space-y-6">
            <h3 className="text-2xl font-bold text-gray-800 flex items-center gap-2 pb-4 border-b-2 border-rose-200">
              <Calendar className="w-6 h-6 text-rose-600" />
              Partnership Details
            </h3>

            <div className="space-y-2" data-error={!!errors.partnership_frequency}>
              <label className="block text-sm font-semibold text-gray-700">
                Partnership Frequency <span className="text-red-500">*</span>
              </label>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {["Weekly", "Monthly", "Quarterly", "Yearly"].map((freq) => (
                  <button
                    key={freq}
                    type="button"
                    onClick={() => handleInputChange("partnership_frequency", freq)}
                    className={`py-3 px-4 rounded-xl font-medium transition-all border-2 ${formData.partnership_frequency === freq
                      ? "bg-rose-500 text-white border-rose-600 shadow-lg"
                      : "bg-gray-50 text-gray-700 border-gray-200 hover:border-rose-300"
                      }`}
                  >
                    {freq}
                  </button>
                ))}
              </div>
              {errors.partnership_frequency && <p className="text-red-500 text-sm">{errors.partnership_frequency}</p>}
            </div>

            <div className="space-y-2" data-error={!!errors.currency}>
              <label className="block text-sm font-semibold text-gray-700">
                Select Currency <span className="text-red-500">*</span>
              </label>
              <p className="text-xs text-gray-500 mb-2">
                Choose your preferred currency. Amounts will be automatically converted from USD.
              </p>
              <select
                value={formData.currency}
                onChange={(e) => handleInputChange("currency", e.target.value)}
                className={`w-full px-4 py-3 rounded-xl border-2 ${errors.currency ? "border-red-400" : "border-gray-200"} focus:border-rose-500 focus:ring-4 focus:ring-rose-100 outline-none transition-all bg-white`}
              >
                {currencies.map((curr) => (
                  <option key={curr.code} value={curr.code}>
                    {curr.symbol} - {curr.name} ({curr.code})
                  </option>
                ))}
              </select>
              {errors.currency && <p className="text-red-500 text-sm">{errors.currency}</p>}
            </div>

            <div className="space-y-2" data-error={!!errors.sponsorship_amount}>
              <label className="block text-sm font-semibold text-gray-700">
                Sponsorship Amount <span className="text-red-500">*</span>
              </label>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {amountOptions.map((amount) => {
                  const convertedAmount = getConvertedAmount(amount, formData.currency)
                  return (
                    <button
                      key={amount}
                      type="button"
                      onClick={() => {
                        setFormData(prev => ({
                          ...prev,
                          sponsorship_amount: amount.toString(),
                          custom_amount: "",
                        }))
                      }}
                      className={`py-4 px-4 rounded-xl font-bold transition-all border-2 ${formData.sponsorship_amount === amount.toString()
                        ? "bg-gradient-to-br from-rose-500 to-pink-600 text-white border-rose-600 shadow-lg scale-105"
                        : "bg-gray-50 text-gray-700 border-gray-200 hover:border-rose-300 hover:scale-105"
                        }`}
                    >
                      <div className="flex items-center justify-center gap-1">
                        <span className="text-xl">{selectedCurrency?.symbol}</span>
                        <span className="text-2xl">{convertedAmount}</span>
                      </div>
                    </button>
                  )
                })}
              </div>
              {errors.sponsorship_amount && <p className="text-red-500 text-sm">{errors.sponsorship_amount}</p>}
            </div>

            <div className="space-y-2" data-error={!!errors.custom_amount}>
              <label className="block text-sm font-semibold text-gray-700">Or Specify Your Own Amount</label>
              <div className="relative">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 font-bold text-lg">
                  {selectedCurrency?.symbol}
                </span>
                <input
                  type="number"
                  value={formData.custom_amount}
                  onChange={(e) => {
                    const val = e.target.value
                    setFormData(prev => ({
                      ...prev,
                      custom_amount: val,
                      sponsorship_amount: "custom",
                    }))
                  }}
                  className={`w-full pl-12 pr-4 py-3 rounded-xl border-2 ${errors.custom_amount ? "border-red-400" : "border-gray-200"} focus:border-rose-500 focus:ring-4 focus:ring-rose-100 outline-none transition-all`}
                  placeholder="Enter custom amount"
                  min="1"
                />
              </div>
              {errors.custom_amount && <p className="text-red-500 text-sm">{errors.custom_amount}</p>}
            </div>
          </div>

          {/* Impact Statement */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4 }}
            className="bg-gradient-to-r from-rose-50 to-pink-50 rounded-2xl p-6 border-2 border-rose-200"
          >
            <div className="flex items-start gap-4">
              <Sparkles className="w-8 h-8 text-rose-600 flex-shrink-0" />
              <div>
                <h4 className="font-bold text-gray-800 mb-2">Your Impact</h4>
                <p className="text-gray-700 text-sm">
                  Every contribution helps us reach more souls, produce quality content, support our community, and
                  expand God's Kingdom. Thank you for being a vital part of our mission!
                </p>
              </div>
            </div>
          </motion.div>

          {/* Submit Button */}
          <motion.button
            type="submit"
            disabled={isSubmitting}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            className="w-full bg-gradient-to-r from-rose-500 to-pink-600 text-white py-4 rounded-xl font-bold text-lg shadow-lg hover:shadow-xl transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {isSubmitting ? (
              <>
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{ duration: 1, repeat: Number.POSITIVE_INFINITY, ease: "linear" }}
                  className="w-6 h-6 border-3 border-white border-t-transparent rounded-full"
                />
                Processing...
              </>
            ) : (
              <>
                <Heart className="w-5 h-5 fill-white" />
                Become a Partner
                <ArrowRight className="w-5 h-5" />
              </>
            )}
          </motion.button>
        </motion.form>
      </div>
    </div>
  )
}
